import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type AdminUser, type UserRole, ApiError } from '../../lib/api.ts';

export function AdminAdmins() {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me() });
  const list = useQuery({ queryKey: ['admins'], queryFn: () => api.listAdmins() });
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState<AdminUser | null>(null);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-semibold">Interne brukere</h1>
        <button
          className="bg-sky-600 hover:bg-sky-500 px-3 py-2 rounded text-sm font-semibold"
          onClick={() => {
            setShowNew(true);
            setEditing(null);
          }}
        >
          + Ny bruker
        </button>
      </div>

      {showNew && (
        <NewUserForm
          onDone={() => {
            setShowNew(false);
            qc.invalidateQueries({ queryKey: ['admins'] });
          }}
        />
      )}

      {editing && (
        <EditUserForm
          user={editing}
          isSelf={editing.id === me.data?.id}
          onDone={() => {
            setEditing(null);
            qc.invalidateQueries({ queryKey: ['admins'] });
          }}
        />
      )}

      {list.isLoading ? (
        <p className="text-slate-500 text-sm">Laster…</p>
      ) : (
        <ul className="divide-y divide-slate-800 border border-slate-800 rounded-xl overflow-hidden">
          {(list.data ?? []).map((a) => {
            const isSelf = a.id === me.data?.id;
            return (
              <li key={a.id} className="p-3 bg-slate-900/30">
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold flex items-center gap-2 flex-wrap">
                      <span className="truncate">{a.username}</span>
                      <RoleBadge role={a.role} />
                      {isSelf && (
                        <span className="text-xs bg-sky-600/30 text-sky-300 px-2 py-0.5 rounded">
                          deg
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-400 truncate">
                      {a.fullName && <span>{a.fullName}</span>}
                      {a.fullName && a.email && <span> • </span>}
                      {a.email && <span>{a.email}</span>}
                      {!a.fullName && !a.email && (
                        <span className="italic">ingen opplysninger</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setEditing(a);
                      setShowNew(false);
                    }}
                    className="text-xs text-sky-400"
                  >
                    Rediger
                  </button>
                  <DeleteButton user={a} disabled={isSelf} />
                </div>
              </li>
            );
          })}
          {list.data?.length === 0 && (
            <li className="p-6 text-center text-slate-500 text-sm">Ingen brukere enda.</li>
          )}
        </ul>
      )}

      <p className="text-xs text-slate-500 mt-3">
        💡 Du kan ikke slette eller degradere deg selv, og systemet krever minst én admin-bruker.
      </p>
    </div>
  );
}

function RoleBadge({ role }: { role: UserRole }) {
  if (role === 'admin') {
    return (
      <span className="text-xs bg-purple-600/30 text-purple-300 px-2 py-0.5 rounded">admin</span>
    );
  }
  return (
    <span className="text-xs bg-emerald-600/30 text-emerald-300 px-2 py-0.5 rounded">
      registrar
    </span>
  );
}

function DeleteButton({ user, disabled }: { user: AdminUser; disabled: boolean }) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => api.deleteAdmin(user.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admins'] }),
    onError: (e: ApiError) => alert(e.message),
  });

  return (
    <button
      disabled={disabled || mut.isPending}
      onClick={() => {
        if (confirm(`Slette brukeren '${user.username}'? Dette kan ikke angres.`)) {
          mut.mutate();
        }
      }}
      className="text-xs text-red-400 disabled:text-slate-600 disabled:cursor-not-allowed"
    >
      Slett
    </button>
  );
}

function NewUserForm({ onDone }: { onDone: () => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<UserRole>('registrar');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      api.createAdmin({
        username,
        password,
        role,
        fullName: fullName || null,
        email: email || null,
      }),
    onSuccess: onDone,
    onError: (e: ApiError) => setErr(e.message),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mut.mutate();
      }}
      className="mb-4 bg-slate-900/60 border border-slate-800 rounded-xl p-4 space-y-3"
    >
      <h2 className="font-semibold">Ny bruker</h2>
      {err && (
        <div className="text-red-300 bg-red-900/30 border border-red-800 rounded p-2 text-sm">
          {err}
        </div>
      )}

      <div>
        <label className="block text-xs text-slate-400 mb-1">Rolle *</label>
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as UserRole)}
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
        >
          <option value="registrar">Registrar — egen båt og påmelding</option>
          <option value="admin">Admin — full tilgang</option>
        </select>
      </div>
      <div>
        <label className="block text-xs text-slate-400 mb-1">Brukernavn *</label>
        <input
          required
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg font-mono"
          autoComplete="off"
        />
      </div>
      <div>
        <label className="block text-xs text-slate-400 mb-1">Passord * (minst 8 tegn)</label>
        <input
          required
          type="password"
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
          autoComplete="new-password"
        />
      </div>
      <div>
        <label className="block text-xs text-slate-400 mb-1">Fullt navn</label>
        <input
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
        />
      </div>
      <div>
        <label className="block text-xs text-slate-400 mb-1">E-post</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
        />
      </div>

      <div className="flex gap-2 pt-2">
        <button
          type="submit"
          disabled={mut.isPending}
          className="flex-1 bg-sky-600 hover:bg-sky-500 py-3 rounded font-semibold disabled:opacity-60"
        >
          {mut.isPending ? 'Oppretter…' : 'Opprett bruker'}
        </button>
        <button
          type="button"
          onClick={onDone}
          className="px-4 py-3 bg-slate-800 hover:bg-slate-700 rounded font-semibold"
        >
          Avbryt
        </button>
      </div>
    </form>
  );
}

function EditUserForm({
  user,
  isSelf,
  onDone,
}: {
  user: AdminUser;
  isSelf: boolean;
  onDone: () => void;
}) {
  const [role, setRole] = useState<UserRole>(user.role);
  const [fullName, setFullName] = useState(user.fullName ?? '');
  const [email, setEmail] = useState(user.email ?? '');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      api.updateAdmin(user.id, {
        role: role !== user.role ? role : undefined,
        fullName: fullName || null,
        email: email || null,
        password: password || undefined,
      }),
    onSuccess: onDone,
    onError: (e: ApiError) => setErr(e.message),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mut.mutate();
      }}
      className="mb-4 bg-slate-900/60 border border-slate-800 rounded-xl p-4 space-y-3"
    >
      <h2 className="font-semibold">
        Rediger <span className="font-mono">{user.username}</span>
        {isSelf && <span className="ml-2 text-xs text-sky-300">(deg selv)</span>}
      </h2>
      {err && (
        <div className="text-red-300 bg-red-900/30 border border-red-800 rounded p-2 text-sm">
          {err}
        </div>
      )}

      <div>
        <label className="block text-xs text-slate-400 mb-1">Rolle</label>
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as UserRole)}
          disabled={isSelf}
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg disabled:opacity-60"
        >
          <option value="registrar">Registrar</option>
          <option value="admin">Admin</option>
        </select>
        {isSelf && <p className="text-xs text-slate-500 mt-1">Du kan ikke endre din egen rolle.</p>}
      </div>

      <div>
        <label className="block text-xs text-slate-400 mb-1">Fullt navn</label>
        <input
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
        />
      </div>
      <div>
        <label className="block text-xs text-slate-400 mb-1">E-post</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
        />
      </div>
      <div>
        <label className="block text-xs text-slate-400 mb-1">
          Nytt passord (la stå tomt for å beholde)
        </label>
        <input
          type="password"
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
          autoComplete="new-password"
        />
        {!isSelf && password && (
          <p className="text-xs text-amber-400 mt-1">
            Ved passordbytte logges denne brukeren ut av alle aktive sesjoner.
          </p>
        )}
      </div>

      <div className="flex gap-2 pt-2">
        <button
          type="submit"
          disabled={mut.isPending}
          className="flex-1 bg-sky-600 hover:bg-sky-500 py-3 rounded font-semibold disabled:opacity-60"
        >
          {mut.isPending ? 'Lagrer…' : 'Lagre'}
        </button>
        <button
          type="button"
          onClick={onDone}
          className="px-4 py-3 bg-slate-800 hover:bg-slate-700 rounded font-semibold"
        >
          Avbryt
        </button>
      </div>
    </form>
  );
}
